import React, { useEffect, useMemo } from 'react'

import { UnrecoverableErrorStore } from './store'

import { VeneerIcon } from '@/components/shared/VeneerIcon/VeneerIcon'

import { ENTRIES } from '@/constants/content-stack'
import { SPLUNK_RUM_FIELDS } from '@/constants/splunk'
import { ErrorCode } from '@/errors/classes/ErrorCode'
import { ExitButton } from '@/pages/UnrecoverableError/components/ExitButton'
import { InstructionsList } from '@/pages/UnrecoverableError/components/InstructionsList'
import { parser } from '@/pages/UnrecoverableError/parser'
import {
  Container,
  Content,
  Footer,
  Footnote,
  Header,
} from '@/pages/UnrecoverableError/UnrecoverableError.styled'
import { UnrecoverableErrorPageAnalytics } from '@/pages/UnrecoverableError/UnrecoverableErrorPage.analytics'
import { useContentStack } from '@/services/content-stack'
import { useSplunkRum } from '@/services/splunk-rum'
import { useDependencyManagerContext } from '@foreign-clientos/dependency-manager'

type UnrecoverableErrorPageProps = Readonly<{
  error: unknown
}>

export function UnrecoverableErrorPage({
  error: unknownError,
}: UnrecoverableErrorPageProps) {
  const { translate } = useDependencyManagerContext()
  const { analyticsConfig } = UnrecoverableErrorStore.use

  const error = useMemo(
    () =>
      unknownError instanceof ErrorCode
        ? unknownError
        : new ErrorCode(undefined, unknownError),
    [unknownError],
  )

  const { publishRumEvent } = useSplunkRum()
  const { entry } = useContentStack({
    entryParams: ENTRIES.UNRECOVERABLE_ERROR,
    parser,
    onError: (_, setFallbackEntry) => {
      setFallbackEntry(
        translate('unrecoverableError', {
          returnObjects: true,
          lng: 'en_US',
        }),
      )
    },
  })

  useEffect(() => {
    OobeLogger.error('An unrecoverable error occurred', { error })

    UnrecoverableErrorPageAnalytics.init({
      errorCode: error.errorCode,
      ...analyticsConfig,
    })
    UnrecoverableErrorPageAnalytics.fireScreenDisplayed()
    publishRumEvent({ [SPLUNK_RUM_FIELDS.WORKFLOW]: error.errorCode })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!entry) return <></>

  return (
    <Container data-testid="unrecoverable-error">
      <Content>
        <Header>
          <VeneerIcon
            icon="MinusCircle"
            size={65}
            color="red6"
          />
          <p>{entry.header}</p>
        </Header>
        <InstructionsList />
        <Footnote>
          <span>{entry.errorLabel} </span>
          <span data-testid="error-code">{error.errorCode}</span>
          <div>{entry.errorNote}</div>
        </Footnote>
      </Content>
      <Footer>
        <ExitButton error={error} />
      </Footer>
    </Container>
  )
}
